import React from 'react';
import { find, sumBy } from 'lodash';

import { DonutChart } from '@magoosh/lib/charts';
import { paths } from 'config/path_helpers';
import { titleize, toSentence } from 'utilities/formatting';
import { pluralize } from 'utilities/pluralize';
import { minutesFormattedAsShortString } from 'utilities/time';

import StatisticData from '@test_prep/app/pages/analytics/components/statistic_data';
import predeterminedColors from '@test_prep/app/pages/analytics/predetermined_colors';

import loadingData from './loading_data';

import colors from '@magoosh/lib/styles/colors.module.scss';

interface Props {
  examName: string;
  filters: any;
}

const FlashcardsMasteredChart: React.FC<Props> = (props) => {
  const loadingPlaceholder = (
    <DonutChart
      chartProps={{ animate: false }}
      data={loadingData(props.examName, props.filters.sections)}
      legendPosition="bottom"
      size={150}
    />
  );

  const formatStatisticData = (statisticData) => {
    return statisticData.map((datum) => {
      const formattedData = {
        label: `${datum.name} Mastered`,
        name: datum.name,
        slug: datum.slug,
        value: datum.value
      } as any;
      const predeterminedColor = predeterminedColors(datum.name, props.examName);

      if (predeterminedColor) {
        formattedData.color = predeterminedColor;
      }

      return formattedData;
    });
  };

  if (!props.filters) {
    return loadingPlaceholder;
  } else {
    return (
      <StatisticData
        filters={props.filters}
        formatDataFn={formatStatisticData}
        name="flashcards_mastered"
      >
        <StatisticData.Loading>
          {loadingPlaceholder}
        </StatisticData.Loading>

        {/* @ts-ignore */}
        <FlashcardsMasteredContent color={colors.dataDarkPurple} />
      </StatisticData>
    );
  }
};

interface FlashcardsMasteredContentProps {
  color: string;
  data: {
    label: string;
    slug: string;
    value: number;
  }[];
}

const FlashcardsMasteredContent: React.FC<FlashcardsMasteredContentProps> = (props) => {
  const deckSetLink = (deckSet) => {
    return `<a class="text-gray-light" href="${paths.flashcards(deckSet.slug)}">${deckSet.name}</a>`;
  };

  const formatValue = (value) => {
    return pluralize(value, 'card');
  };

  return (
    <div className="align-items-center d-flex flex-column gap-xs">
      <DonutChart
        chartProps={{ animate: false }}
        data={props.data}
        interior={(data) => (
          <strong>
            <span style={{ color: props.color, fontSize: 150 * 0.25 }}>
              {sumBy(data, 'value')}
            </span>
          </strong>
        )}
        formatValueFn={formatValue}
        legendPosition="bottom"
        size={150}
      />

      {
        props.data.length > 0 && (
          <div>
            <small className="text-gray-light">
              Study
              {' '}
              <span
                dangerouslySetInnerHTML={{
                  __html: toSentence(
                    props.data,
                    'or',
                    (deckSet) => deckSetLink(deckSet)
                  )
                }}
              />
              {' '}
              flashcards
            </small>
          </div>
        )
      }
    </div>
  );
};
export default FlashcardsMasteredChart;
